// Nederlandse teksten voor het states-deel. Dezelfde sleutels als states.en.ts;
// de typecheck faalt als er een ontbreekt of bijkomt.
//
// Dezelfde harde regel als in ../states.ts, en states.test.ts controleert hem
// ook voor deze teksten: geen label of uitleg zegt dat er iets geboekt,
// verwerkt, betaald of afgeletterd is, tenzij de opdracht `confirmed` is — en
// dan nog zegt hij wat het ontvangstbewijs zegt. Een concept is een concept.
//
// Woordkeus: operation = opdracht, status check = statuscontrole, receipt (van
// het boekhoudpakket) = ontvangstbewijs, receipt (kassabon) = bon.

import type { Message } from './en.ts';
import type { statesEn } from './states.en.ts';

export const statesNl: Readonly<Record<keyof typeof statesEn, Message>> = {
  // --- opdracht (app.operation_state)
  'state.operation.queued.label': 'In de wachtrij',
  'state.operation.queued.explanation': 'Goedgekeurd en wacht op versturen. Er is nog niets verstuurd.',
  'state.operation.submitting.label': 'Bezig met versturen',
  'state.operation.submitting.explanation': 'Het verzoek ligt bij het boekhoudpakket. De uitkomst is nog niet bekend.',
  'state.operation.confirmed.label': 'Bevestigd',
  'state.operation.confirmed.explanation': 'Het boekhoudpakket heeft dit bevestigd, en het ontvangstbewijs hieronder is het bewijs.',
  'state.operation.failed_safe.label': 'Niet doorgevoerd',
  'state.operation.failed_safe.explanation':
    'Het boekhoudpakket toonde aan dat er niets is doorgevoerd, dus dit kan veilig opnieuw.',
  'state.operation.unknown.label': 'Uitkomst onbekend',
  'state.operation.unknown.explanation':
    'We konden niet vaststellen of dit is doorgevoerd. Het wordt niet opnieuw verstuurd tot een statuscontrole antwoord geeft.',
  'state.operation.needs_review.label': 'Controleren',
  'state.operation.needs_review.explanation':
    'Iemand moet hiernaar kijken. Of de statuscontrole kon niet vaststellen wat er gebeurde, of er stond al een overeenkomende boeking in het boekhoudpakket.',
  // --- document (app.document_state)
  'state.document.received.label': 'Ontvangen',
  'state.document.received.explanation': 'Wacht om gelezen te worden.',
  'state.document.quarantined.label': 'In quarantaine',
  'state.document.quarantined.explanation': 'Tegengehouden vóór de verwerking. Zie de blokkades hieronder.',
  'state.document.extracting.label': 'Wordt gelezen',
  'state.document.extracting.explanation': 'Het uitlezen loopt.',
  'state.document.needs_review.label': 'Controleren',
  'state.document.needs_review.explanation': 'Iets aan dit document hield de automatische voorbereiding tegen.',
  'state.document.ready.label': 'Klaar voor goedkeuring',
  'state.document.ready.short': 'Klaar',
  'state.document.ready.explanation': 'Er staat een voorstel klaar. Er wordt niets verstuurd tot je het goedkeurt.',
  'state.document.approved.label': 'Goedgekeurd',
  'state.document.approved.explanation': 'Je hebt dit goedgekeurd. De uitkomst in het boekhoudpakket staat bij de opdracht.',
  'state.document.excluded.label': 'Uitgesloten',
  'state.document.excluded.explanation': 'Uitgesloten van de verwerking. Er is niets verwijderd in het boekhoudpakket.',
  'state.document.imported.label': 'Afschrift ingelezen',
  'state.document.imported.explanation':
    'De regels zijn opgeslagen en eventuele kandidaat-matches wachten bij Bankafletteren. Er is niets naar het boekhoudpakket verstuurd.',
  // --- een bevestigde boeking, naar wat het ontvangstbewijs zegt
  'state.bookingReceipt.posted.label': 'Geboekt',
  'state.bookingReceipt.posted.explanation':
    'Geboekt: het boekhoudpakket heeft het bevestigd, en het ontvangstbewijs staat bij de opdracht.',
  'state.bookingReceipt.draft_created.label': 'Concept in het boekhoudpakket',
  'state.bookingReceipt.draft_created.explanation':
    'Er staat een concept in het boekhoudpakket. Het is pas definitief als iemand het daar definitief maakt.',
  // --- bankkoppeling (app.match_status)
  'state.match.candidate.label': 'Mogelijke match',
  'state.match.candidate.explanation': 'Bewijs om zelf te beoordelen. Er wordt niets verstuurd tot je het goedkeurt.',
  'state.match.approved.label': 'Goedgekeurd',
  'state.match.approved.explanation': 'Je hebt deze match goedgekeurd. De uitkomst in het boekhoudpakket staat bij de opdracht.',
  'state.match.executed.label': 'Verstuurd',
  'state.match.executed.explanation':
    'Er is iets met deze match gedaan. Of de boekhouding het ermee eens is, staat bij de opdracht hieronder.',
  'state.match.stale.label': 'Bron gewijzigd',
  'state.match.stale.explanation': 'De bankregel of de factuur is veranderd sinds deze kandidaat werd berekend.',
  'state.match.rejected.label': 'Afgewezen',
  'state.match.rejected.explanation': 'Niet goedgekeurd.',
  // --- boekingsvoorstel (app.proposal_status)
  'state.proposal.draft.label': 'Concept',
  'state.proposal.draft.explanation': 'Nog niet klaar voor een besluit.',
  'state.proposal.needs_review.label': 'Controleren',
  'state.proposal.needs_review.explanation': 'Een controle is niet geslaagd. Corrigeer het of sluit het document uit.',
  'state.proposal.ready.label': 'Klaar voor goedkeuring',
  'state.proposal.ready.explanation': 'Controles afgerond. Pas jouw goedkeuring verstuurt het.',
  'state.proposal.approved.label': 'Goedgekeurd',
  'state.proposal.approved.explanation': 'Goedgekeurd. De uitkomst in het boekhoudpakket zie je bij de opdracht.',
  'state.proposal.stale.label': 'Bron gewijzigd',
  'state.proposal.stale.explanation': 'De boekhouding is veranderd sinds dit werd voorbereid. Er is een nieuw besluit nodig.',
  'state.proposal.superseded.label': 'Vervangen',
  'state.proposal.superseded.explanation': 'Een nieuwere versie heeft deze vervangen.',
  'state.proposal.rejected.label': 'Afgewezen',
  'state.proposal.rejected.explanation': 'Niet goedgekeurd.',
  // --- klantverzoek (app.request_state)
  'state.request.draft.label': 'Concept',
  'state.request.draft.explanation': 'Nog niemand heeft dit goedgekeurd, en er is niets verstuurd.',
  'state.request.approved.label': 'Goedgekeurd om te versturen',
  'state.request.approved.explanation':
    'Je hebt precies dit bericht en deze ontvanger goedgekeurd. Of het vertrokken is, staat bij de opdracht hieronder.',
  'state.request.sending.label': 'Bezig met versturen',
  'state.request.sending.explanation': 'Overgedragen aan de mailserver. De uitkomst is nog niet bekend.',
  'state.request.sent.label': 'Verstuurd',
  'state.request.sent.explanation':
    'De mailserver heeft het aangenomen. Er kan nog een bounce komen, en die wordt apart getoond.',
  'state.request.delivery_unknown.label': 'Bezorging onbekend',
  'state.request.delivery_unknown.explanation':
    'We konden niet vaststellen of dit de klant bereikte. Het wordt niet vanzelf opnieuw verstuurd.',
  'state.request.waiting.label': 'Wacht op antwoord',
  'state.request.waiting.explanation': 'Gevraagd, en er is nog niets teruggekomen.',
  'state.request.reply_received.label': 'Antwoord binnen',
  'state.request.reply_received.explanation':
    'Er is iets teruggekomen en dat is bijgevoegd. Dat is geen bewijs dat het ontbrekende document er is — iemand moet kijken.',
  'state.request.resolved.label': 'Afgehandeld',
  'state.request.resolved.explanation': 'Gesloten. Er wordt niets meer van de klant verwacht.',
  'state.request.cancelled.label': 'Geannuleerd',
  'state.request.cancelled.explanation': 'Ingetrokken. Wat al verstuurd is, kan niet worden teruggehaald.',
  // --- verwerkingsgeschiedenis (app.audit_events)
  'state.audit.proposal.revised.label': 'Voorstel aangepast',
  'state.audit.proposal.revised.note': 'Een nieuwe, onveranderlijke versie. Een eerdere goedkeuring geldt niet meer.',
  'state.audit.proposal.approved.label': 'Voorstel goedgekeurd',
  'state.audit.proposal.approved.note': 'Het besluit is vastgelegd en er is één opdracht in de wachtrij gezet.',
  'state.audit.document.excluded.label': 'Document uitgesloten',
  'state.audit.document.excluded.note': 'Uitgesloten van de verwerking. Er is niets verwijderd in het boekhoudpakket.',
  'state.audit.bank_match.approved.label': 'Bankmatch goedgekeurd',
  'state.audit.bank_match.approved.note': 'Het besluit is vastgelegd en er is één opdracht in de wachtrij gezet.',
  'state.audit.operation.probe_requested.label': 'Statuscontrole aangevraagd',
  'state.audit.operation.probe_requested.note': 'We vragen het boekhoudpakket wat er gebeurde. Er is niets opnieuw geschreven.',
  'state.audit.operation.retry_authorised.label': 'Opnieuw proberen toegestaan',
  'state.audit.operation.retry_authorised.note':
    'Alleen toegestaan als het boekhoudpakket aantoonde dat er niets was doorgevoerd.',
  'state.audit.operation.submitted.label': 'Verzoek gedaan aan het boekhoudpakket',
  'state.audit.operation.submitted.note': 'Of het is aangekomen, meldt de opdracht, niet deze regel.',
  'state.audit.operation.confirmed.label': 'Bevestiging van het boekhoudpakket',
  'state.audit.operation.confirmed.note':
    'Er kwam een ontvangstbewijs terug. De opdracht zegt of het een boeking of een concept was.',
  'state.audit.operation.failed_safe.label': 'Gestopt voordat er iets veranderde',
  'state.audit.operation.failed_safe.note':
    'Er is niets verstuurd, of het boekhoudpakket toonde aan dat er niets was doorgevoerd. Geen haast bij.',
  'state.audit.operation.unknown.label': 'Uitkomst niet vastgesteld',
  'state.audit.operation.unknown.note':
    'Een verzoek kan het boekhoudpakket hebben bereikt. Er staat een statuscontrole in de wachtrij; er is niets opnieuw verstuurd.',
  'state.audit.operation.unknown_after_crash.label': 'Uitkomst niet vastgesteld na een herstart',
  'state.audit.operation.unknown_after_crash.note':
    'Een worker stopte midden in een verzoek. Op deze route wordt niets opnieuw verstuurd; een statuscontrole beslist.',
  'state.audit.operation.duplicate_present.label': 'Er stond al een overeenkomende boeking',
  'state.audit.operation.duplicate_present.note':
    'Iets anders had die al aangemaakt. Wij hebben niets aangemaakt en geen ontvangstbewijs vastgelegd.',
  'state.audit.operation.needs_review.label': 'Wacht op een mens',
  'state.audit.operation.needs_review.note':
    'Het boekhoudpakket kon niet aantonen wat er gebeurde. Zoek het met de hand uit.',
  'state.audit.extraction.completed.label': 'Voorstel voorbereid',
  'state.audit.extraction.completed.note':
    'Voorbereid op basis van het document. Er komt niets in het boekhoudpakket tot iemand het goedkeurt.',
  'state.audit.extraction.blocked.label': 'Voorbereiding gestopt op een bevinding',
  'state.audit.extraction.blocked.note':
    'Het document is gelezen en iets klopte niet. De bevindingen staan bij het document.',
  'state.audit.extraction.abandoned.label': 'Voorbereiding opgegeven',
  'state.audit.extraction.abandoned.note':
    'Het document kon na herhaalde pogingen niet worden gelezen. Het wacht op een mens.',
  'state.audit.document.source_url_issued.label': 'Bron geopend om te bekijken',
  'state.audit.document.source_url_issued.note': 'Deze persoon kreeg een kortlevende link naar het opgeslagen bestand.',
  'state.audit.document.source_downloaded.label': 'Bronbestand gelezen',
  'state.audit.document.source_downloaded.note': 'Het opgeslagen document is via die link opgehaald.',
  'state.audit.statement.imported.label': 'Bankafschrift ingelezen',
  'state.audit.statement.imported.note':
    'De saldi klopten. Eventuele kandidaat-matches staan bij Bankafletteren; er is niets naar het boekhoudpakket verstuurd.',
  'state.audit.statement.blocked.label': 'Bankafschrift tegengehouden',
  'state.audit.statement.blocked.note':
    'Het bestand is gelezen en klopte niet — een afschrift dat niet sluit of een onbekende rekening. Er is niets opgeslagen.',
  'state.audit.statement.abandoned.label': 'Inlezen bankafschrift opgegeven',
  'state.audit.statement.abandoned.note':
    'Het afschrift kon na herhaalde pogingen niet worden ingelezen. Het wacht op een mens.',
  'state.audit.connection.sync_succeeded.label': 'Gesynchroniseerd met het boekhoudpakket',
  'state.audit.connection.sync_succeeded.note': 'Stamgegevens zijn gelezen. Er is niets geschreven.',
  'state.audit.connection.sync_failed.label': 'Synchronisatie mislukt',
  'state.audit.connection.sync_failed.note':
    'Het boekhoudpakket kon niet worden gelezen. Bij Koppelingen zie je de status.',
  'state.audit.counterparty.classified.label': 'Tegenpartij ingedeeld',
  'state.audit.counterparty.classified.note':
    'Een beoordelaar legde vast of deze partij een eenmalige of een vaste leverancier is. Er is geen relatie aangemaakt.',
  // --- ontvangstbewijs (app.receipt_state)
  'state.receipt.draft_created': 'Concept aangemaakt in het boekhoudpakket',
  'state.receipt.posted': 'Geboekt in het boekhoudpakket',
  'state.receipt.allocated': 'Toegewezen aan de factuur',
  'state.receipt.file_attached': 'Bestand bijgevoegd',
  'state.receipt.import_accepted': 'Import aangenomen',
  // --- wie handelde (app.actor_kind)
  'state.actor.user': 'Persoon',
  'state.actor.system': 'Automatisch',
  'state.actor.agency_operator': 'Medewerker van het kantoor',
  // --- blokkades
  'state.blocker.quarantined': 'Tegengehouden voor verwerking',
  'state.blocker.proposal_stale': 'De boekhouding is veranderd nadat dit voorstel werd voorbereid',
  'state.blocker.COUNTERPARTY_UNCLASSIFIED': 'Deel deze partij in: een vaste zakelijke leverancier, of een eenmalige aankoop',
  'state.blocker.ONE_OFF_RELATION_NOT_CONFIGURED':
    'Deze administratie heeft geen eenmalige tegenpartij ingesteld om op te boeken',
  'state.blocker.SOURCE_UNREADABLE': 'Het document kon niet worden gelezen',
  'state.blocker.STATEMENT_UNBALANCED':
    'Het afschrift sluit niet: beginsaldo plus de regels is niet het eindsaldo. Er is niets ingelezen',
  'state.blocker.MT940_UNPARSEABLE': 'Dit is geen afschrift dat gelezen kon worden',
  'state.blocker.MT940_NO_STATEMENTS': 'Het bestand bevat geen afschrift',
  'state.blocker.BANK_ACCOUNT_NOT_CONFIGURED':
    'Deze rekening hoort niet bij de bankrekeningen van deze administratie. Er is niets ingelezen — voeg de rekening toe als hij klopt',
  'state.blocker.BANK_ACCOUNT_NOT_IDENTIFIED':
    'Het afschrift noemt geen IBAN waartegen deze administratie gecontroleerd kan worden',
  'state.blocker.NO_EXTERNAL_MAPPING': 'Deze administratie is niet gekoppeld aan haar boekhoudpakket',
  'state.blocker.IMPORT_ABANDONED': 'Het inlezen is een paar keer geprobeerd en kon niet worden afgerond',
  'state.blocker.OCR_LOW_CONFIDENCE':
    'De tekst uit deze afbeelding is te onzeker om een boeking van voor te bereiden. Er is niets naar het model gestuurd',
  'state.blocker.VAT_NUMBER_MALFORMED': 'Btw-nummer lijkt verkeerd gelezen',
  'state.blocker.VAT_NUMBER_CHECKSUM': 'Het controlegetal klopt niet, controleer dit nummer',
  'state.blocker.RECEIPT_NOT_POSTABLE': 'Bonnen kunnen nog niet naar e-Boekhouden',
  'state.blocker.GATEWAY_ROUTE_UNCONFIRMED':
    'Niet te bevestigen waar het model dit verwerkte, dus het antwoord is niet gebruikt',
  'state.blocker.HELD_ANSWER_TEXT_CHANGED':
    'Het document leest nu anders dan toen het werd gestuurd, dus het bewaarde antwoord is niet gebruikt. Lees het opnieuw',
  'state.blocker.GATEWAY_ROUTE_CHANGED_WHILE_PENDING':
    'De goedgekeurde modelroute veranderde terwijl het antwoord werd bewaard, dus het is niet gebruikt. Lees het opnieuw',
  'state.blocker.CREDIT_NOTE_NOT_POSTABLE': 'Creditnota’s kunnen nog niet naar e-Boekhouden',
  'state.blocker.CREDIT_NOTE_SIGNS_MIXED':
    'Sommige bedragen staan er negatief en sommige positief; een creditnota is het een of het ander',
  'state.blocker.PAYMENT_METHOD_UNKNOWN': 'Op de bon staat niet hoe er is afgerekend: kies contant of pin/bank',
  'state.blocker.CARD_RECEIPT_LIVE_FEED':
    'Gepind in een administratie met een live bankkoppeling: boek hem via de bankregel in e-Boekhouden, zodat hij niet dubbel in de boeken komt.',
  'state.blocker.CASH_LEDGER_NOT_CONFIGURED':
    'Deze administratie heeft geen kasrekening ingesteld om een contante bon op te boeken',
  'state.blocker.BANK_LEDGER_NOT_CONFIGURED':
    'Deze administratie heeft geen bankrekening in het grootboek ingesteld om een pinbon op te boeken',
  'state.blocker.RECEIPT_BANK_LINE_NOT_FOUND':
    'Er is nog geen ingelezen bankregel die bij deze pinbetaling past. Hij wordt opnieuw gelezen bij het volgende afschrift',
  'state.blocker.RECEIPT_BANK_LINE_AMBIGUOUS':
    'Meerdere ingelezen bankregels passen bij deze pinbetaling; niet te zeggen welke',
  'state.blocker.RECEIPT_JOURNAL_NOT_CONFIGURED': 'Deze administratie heeft geen dagboek ingesteld voor bonnen',
  'state.blocker.RECEIPT_AMOUNT_NOT_POSITIVE': 'Het totaal van een bon moet positief zijn',
  'state.blocker.PAYMENT_LEDGER_UNKNOWN':
    'De ingestelde kas- of bankrekening staat niet in het rekeningschema van het pakket',
  // --- waarom een document niet gelezen kon worden
  'state.reason.NO_TEXT_LAYER': 'Deze pdf heeft geen tekstlaag — het is een scan, en heeft OCR nodig',
  'state.reason.NO_TEXT_FOUND': 'OCR vond er geen tekst in',
  'state.reason.IMAGE_UNREADABLE': 'De afbeelding kon niet worden geopend',
  'state.reason.OPEN_ITEMS_UNAVAILABLE': 'Het boekhoudpakket gaf zijn openstaande posten niet',
  'state.reason.NO_TEXT_ROUTE': 'Er is nog geen lezer voor dit bestandstype',
  'state.reason.PDF_UNREADABLE': 'De pdf kon niet worden ontleed',
  'state.reason.READER_TIMEOUT': 'Het lezen duurde te lang en is gestopt',
  'state.reason.READER_LIMIT_EXCEEDED': 'Het bestand had meer geheugen of tijd nodig dan een document hoort te hebben',
  'state.reason.READER_OUTPUT_TOO_LARGE': 'Het bestand leverde veel meer tekst op dan een document heeft',
  'state.reason.CONTENT_HASH_MISMATCH': 'Het opgeslagen bestand komt niet overeen met de versie die binnenkwam',
  'state.reason.ROUTE_NOT_REPORTED': 'de aanbieder heeft de regio nooit gemeld',
  'state.reason.REGION_NOT_APPROVED': 'de aanbieder meldde een regio buiten de goedkeuring',
  'state.reason.SERVED_BY_NOT_APPROVED': 'de aanbieder meldde een server buiten de goedkeuring',
  'state.reason.MODEL_NOT_APPROVED': 'er antwoordde een ander model dan het goedgekeurde',
  // --- hoe de tekst verkregen is
  'state.textRoute.plain_text': 'Tekstbestand, gelezen zoals het is',
  'state.textRoute.pdf_text_layer': 'Tekstlaag in de pdf (geen OCR)',
  'state.textRoute.ocr_local': 'OCR van de afbeelding, op deze server',
  // --- mailgebeurtenissen (app.mail_event_kind)
  'state.mail.accepted': 'De mailserver heeft het aangenomen',
  'state.mail.delivered': 'De ontvangende server heeft het aangenomen',
  'state.mail.bounced': 'Het is teruggekomen (bounce)',
  'state.mail.failed': 'Versturen mislukt',
  'state.mail.reply_received': 'Er is een antwoord bijgevoegd',
  // --- een status die deze versie niet kent
  'state.unknown.explanation': 'Deze versie herkent de status die de server meldt niet.',
  // --- namen van opdrachten
  'state.operationName.create_purchase_entry': 'inkoopboeking aanmaken',
  'state.operationName.create_purchase_credit_note': 'inkoopcreditnota aanmaken',
  'state.operationName.create_purchase_receipt': 'inkoopbon aanmaken',
  'state.operationName.settle_credit_note': 'creditnota verrekenen',
  'state.operationName.reconcile_bank_line': 'bankregel afletteren',
  'state.operationName.send_message': 'bericht versturen',
  // --- het paneel Status in het boekhoudpakket
  'state.external.title': 'Status in het boekhoudpakket',
  'state.external.intro': 'Alleen een ontvangstbewijs van het boekhoudpakket bevestigt dat er iets is gebeurd.',
  'state.external.checkStatusOnly':
    'Een statuscontrole is de enige veilige volgende stap. Dit wordt niet automatisch opnieuw geprobeerd, en mag niet opnieuw worden verstuurd.',
  'state.external.step.approved': 'Goedgekeurd',
  'state.external.step.queued': 'In de wachtrij',
  'state.external.step.sending': 'Bezig met versturen',
  'state.external.step.confirmed': 'Bevestigd',
};
